import { useMemo, useState, useEffect, type JSX } from "react";
import { getSupabase } from "@dwelldrop/supabase";
import {
  parseTermsData,
  parsePrivacyData,
  type TermsData,
} from "@dwelldrop/shared";
import TermsModal from "./TermsModal";
import PrivacyModal from "./PrivacyModal";

const VENUE_SUPABASE_STORAGE_KEY = "venuehub_venue_token";

const TERMS_CONSENT_NAME = "terms_and_condition_venue";
const PRIVACY_CONSENT_NAME = "privacy_policy_venue";

function consentDescriptionIsStructuredPolicyJson(raw: string): boolean {
  if (!raw.trim()) return false;
  try {
    const parsed = JSON.parse(raw) as { sections?: unknown };
    return Array.isArray(parsed.sections);
  } catch {
    return false;
  }
}

function plainDescriptionAsTermsData(
  raw: string,
  shell: TermsData,
): TermsData {
  const text = raw.trim();
  if (!text) {
    return shell;
  }
  return {
    ...shell,
    intro: "",
    sections: [{ title: "", content: text }],
  };
}

type ConsentDocumentModalProps = {
  activeModal: "terms" | "privacy" | null;
  onClose: () => void;
  /** Called when the user closes the document or accepts (terms flow), so parents can e.g. tick a checkbox. */
  onAcknowledge?: (which: "terms" | "privacy") => void;
  consentContent?: Record<string, string>;
  consentVersions?: Record<string, number>;
  consentWhatsNew?: Record<string, string>;
  showAcceptButton?: boolean;
  onAccept?: () => void;
  /** Passed to Terms/Privacy modal backdrop (e.g. higher z-index when nested). */
  backdropClassName?: string;
};

export default function ConsentDocumentModal({
  activeModal,
  onClose,
  onAcknowledge,
  consentContent: consentContentProp,
  consentVersions: consentVersionsProp,
  consentWhatsNew: consentWhatsNewProp,
  showAcceptButton,
  onAccept,
  backdropClassName,
}: ConsentDocumentModalProps): JSX.Element | null {
  const [internalContent, setInternalContent] = useState<Record<string, string>>({});
  const [internalVersions, setInternalVersions] = useState<Record<string, number>>({});
  const [internalWhatsNew, setInternalWhatsNew] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeModal) return;
    if (consentContentProp !== undefined) return;

    let cancelled = false;
    setLoading(true);

    async function fetchConsentContent(): Promise<void> {
      const supabase = getSupabase(VENUE_SUPABASE_STORAGE_KEY);
      const { data } = await supabase
        .from("consent_types")
        .select("name, description, version, whats_new")
        .in("name", [TERMS_CONSENT_NAME, PRIVACY_CONSENT_NAME]);
      if (cancelled) return;
      if (data) {
        const contentMap: Record<string, string> = {};
        const versionMap: Record<string, number> = {};
        const whatsNewMap: Record<string, string> = {};
        for (const row of data as {
          name: string;
          description: string;
          version: number;
          whats_new: string | null;
        }[]) {
          contentMap[row.name] = row.description;
          versionMap[row.name] = row.version;
          if (row.whats_new) whatsNewMap[row.name] = row.whats_new;
        }
        setInternalContent(contentMap);
        setInternalVersions(versionMap);
        setInternalWhatsNew(whatsNewMap);
      }
      setLoading(false);
    }

    void fetchConsentContent();
    return () => {
      cancelled = true;
    };
  }, [activeModal, consentContentProp]);

  const consentContent = consentContentProp ?? internalContent;
  const consentVersions = consentVersionsProp ?? internalVersions;
  const consentWhatsNew = consentWhatsNewProp ?? internalWhatsNew;

  const acknowledgeAndClose = (): void => {
    if (activeModal) {
      onAcknowledge?.(activeModal);
    }
    onClose();
  };

  const wrappedOnAccept =
    showAcceptButton && onAccept
      ? () => {
          if (activeModal) {
            onAcknowledge?.(activeModal);
          }
          onAccept();
        }
      : undefined;

  const activeConsentName =
    activeModal === "terms" ? TERMS_CONSENT_NAME : PRIVACY_CONSENT_NAME;

  const rawDescription = consentContent[activeConsentName] ?? "";

  const termsData = useMemo(() => {
    const raw = consentContent[TERMS_CONSENT_NAME] ?? "";
    if (consentDescriptionIsStructuredPolicyJson(raw)) {
      return parseTermsData(raw);
    }
    return plainDescriptionAsTermsData(raw, parseTermsData(""));
  }, [consentContent]);

  const privacyData = useMemo(() => {
    const raw = consentContent[PRIVACY_CONSENT_NAME] ?? "";
    if (consentDescriptionIsStructuredPolicyJson(raw)) {
      return parsePrivacyData(raw);
    }
    return plainDescriptionAsTermsData(raw, parsePrivacyData(""));
  }, [consentContent]);

  const activeDocumentData =
    activeModal === "terms" ? termsData : privacyData;

  if (!activeModal) return null;

  if (loading && !rawDescription) {
    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 ${backdropClassName ?? ""}`}
        onClick={onClose}
      >
        <div
          className="rounded-xl bg-white px-6 py-4 text-sm text-gray-600"
          onClick={(e) => e.stopPropagation()}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (activeModal === "terms") {
    return (
      <TermsModal
        data={activeDocumentData}
        version={consentVersions[TERMS_CONSENT_NAME]}
        whatsNew={consentWhatsNew[TERMS_CONSENT_NAME]}
        onClose={acknowledgeAndClose}
        showAcceptButton={showAcceptButton}
        onAccept={wrappedOnAccept ?? onAccept}
        backdropClassName={backdropClassName}
      />
    );
  }

  return (
    <PrivacyModal
      data={activeDocumentData}
      version={consentVersions[PRIVACY_CONSENT_NAME]}
      whatsNew={consentWhatsNew[PRIVACY_CONSENT_NAME]}
      onClose={acknowledgeAndClose}
      backdropClassName={backdropClassName}
    />
  );
}